import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  MagnifyingGlass, ShieldCheck, CheckCircle, Star, MapPin, Video, HouseSimple, UsersThree,
  ArrowRight, Sparkle, SlidersHorizontal, X, UserCirclePlus,
} from '@phosphor-icons/react';
import Header from '@/components/Header';
import PremiumFooter from '@/components/PremiumFooter';
import { WELLNESS_CATEGORIES, MODES, COUNTRIES, formatPrice, getCategoryById } from '@/data/wellness';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MODE_ICON = { online: Video, in_person: UsersThree, home_visit: HouseSimple };

const ProCard = ({ pro, onView, onBook }) => {
  const category = getCategoryById(pro.category_id);
  const Icon = category?.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-3xl border border-stone-100 overflow-hidden hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 group flex flex-col"
      data-testid={`pro-card-${pro.slug}`}
    >
      {/* Header banner */}
      <div className={`relative h-24 bg-gradient-to-br ${category?.tint || 'from-stone-100 to-stone-200'}`}>
        <div className="absolute inset-0 opacity-30" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(0,0,0,0.06) 1px, transparent 0)', backgroundSize: '14px 14px' }} />
        <div className="absolute -bottom-8 left-6 w-16 h-16 rounded-2xl bg-white shadow-lg border-4 border-white flex items-center justify-center overflow-hidden">
          {pro.hero_image_url ? (
            <img src={pro.hero_image_url} alt={pro.full_name} className="w-full h-full object-cover" />
          ) : (
            Icon && <Icon size={26} weight="duotone" style={{ color: category.color }} />
          )}
        </div>
        {pro.is_top_professional && (
          <div className="absolute top-3 right-3 bg-white/95 backdrop-blur text-[#78350F] text-[10px] font-bold uppercase tracking-[0.14em] px-2.5 py-1 rounded-full flex items-center gap-1 shadow-sm">
            <Star size={10} weight="fill" className="text-[#F59E0B]" /> Top
          </div>
        )}
      </div>

      <div className="pt-11 pb-6 px-6 flex-1 flex flex-col">
        <h3 className="text-lg font-semibold text-[#1C1917] font-heading tracking-tight leading-tight" data-testid={`pro-name-${pro.slug}`}>{pro.full_name}</h3>
        <p className="text-sm text-stone-500 mt-0.5">{pro.professional_type}</p>

        {pro.headline && (
          <p className="text-sm text-stone-600 mt-3 line-clamp-2 leading-relaxed">{pro.headline}</p>
        )}

        {/* Trust badges */}
        <div className="flex flex-wrap gap-1.5 mt-4">
          {pro.nova_verified && (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.14em] bg-[#F0F7F1] text-[#1A4331] px-2 py-0.5 rounded-full">
              <ShieldCheck size={10} weight="fill" /> Nova Verified
            </span>
          )}
          {pro.credential_verified && (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.14em] bg-[#ECFDF5] text-[#065F46] px-2 py-0.5 rounded-full">
              <CheckCircle size={10} weight="fill" /> Credential
            </span>
          )}
        </div>

        {/* Stats row */}
        <div className="flex items-center gap-4 mt-4 text-xs text-stone-500">
          {pro.avg_rating > 0 && (
            <span className="inline-flex items-center gap-1"><Star size={12} weight="fill" className="text-[#F59E0B]" /> {pro.avg_rating.toFixed(1)}</span>
          )}
          {pro.years_experience > 0 && <span>{pro.years_experience} yrs exp</span>}
          {pro.total_bookings > 0 && <span>{pro.total_bookings} sessions</span>}
        </div>

        {/* Location + modes */}
        <div className="flex items-center gap-3 mt-3 text-xs text-stone-500">
          <span className="inline-flex items-center gap-1"><MapPin size={12} weight="fill" /> {pro.city}, {pro.country}</span>
        </div>
        <div className="flex flex-wrap gap-1.5 mt-2">
          {(pro.modes || []).map((m) => {
            const M = MODE_ICON[m];
            const label = MODES.find((x) => x.id === m)?.short || m;
            return (
              <span key={m} className="inline-flex items-center gap-1 text-[11px] bg-stone-50 border border-stone-100 text-stone-600 px-2 py-0.5 rounded-full">
                {M && <M size={11} weight="duotone" />} {label}
              </span>
            );
          })}
        </div>

        {/* Price + CTA */}
        <div className="mt-6 flex items-end justify-between pt-4 border-t border-stone-100">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-stone-400">From</p>
            <p className="text-xl font-semibold text-[#1A4331] font-heading tracking-tight">
              {pro.price_from > 0 ? formatPrice(pro.price_from, pro.currency) : 'On request'}
              <span className="text-xs text-stone-400 font-normal ml-1">/ session</span>
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => onView(pro.slug)}
              className="text-[#1A4331] font-semibold text-sm hover:underline"
              data-testid={`pro-view-${pro.slug}`}
            >
              View
            </button>
            <button
              onClick={() => onBook(pro.slug)}
              className="bg-[#1A4331] text-white px-4 py-2 rounded-full text-sm font-semibold hover:bg-[#2A5A44] transition-all"
              data-testid={`pro-book-${pro.slug}`}
            >
              Book
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const NotifyMeEmpty = ({ activeCount, filters, clearFilters, onInviteClick }) => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { toast.error('Please enter a valid email'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/wellness/notify-me`, {
        email,
        category_id: filters.category || null,
        city: filters.city || null,
        country: filters.country || null,
        mode: filters.mode || null,
      });
      setSubmitted(true);
    } catch (err) { toast.error('Failed to sign up. Try again.'); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="bg-white rounded-3xl border border-stone-100 p-8 md:p-14 text-center" data-testid="empty-state">
      <div className="w-20 h-20 mx-auto rounded-3xl bg-gradient-to-br from-[#F0F7F1] to-[#E8F5E9] flex items-center justify-center mb-6">
        <Sparkle size={32} weight="duotone" className="text-[#1A4331]" />
      </div>
      <h3 className="text-2xl font-semibold text-[#1C1917] font-heading tracking-tight mb-2">
        {activeCount > 0 ? 'No experts match those filters yet' : 'Our wellness experts are on the way'}
      </h3>
      <p className="text-stone-600 leading-relaxed max-w-lg mx-auto mb-6">
        {activeCount > 0
          ? 'Try broadening your search — or get notified when a matching professional joins.'
          : 'Nova Wellness is just launching. Get notified when a verified expert joins, or invite one you know.'}
      </p>

      {submitted ? (
        <div className="max-w-md mx-auto bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-2xl p-4 mb-4" data-testid="notify-me-success">
          <p className="font-semibold text-sm">✓ You&apos;re on the list</p>
          <p className="text-xs mt-1">We&apos;ll email you the moment a matching professional joins Nova Wellness.</p>
        </div>
      ) : (
        <form onSubmit={submit} className="max-w-md mx-auto flex gap-2 mb-4" data-testid="notify-me-form">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Your email"
            className="flex-1 bg-white border border-stone-200 rounded-full px-4 py-2.5 text-sm outline-none focus:border-[#1A4331]"
            required
            data-testid="notify-me-email"
          />
          <button type="submit" disabled={submitting} className="bg-[#1A4331] text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-[#2A5A44] disabled:opacity-50" data-testid="notify-me-submit">
            {submitting ? 'Saving...' : 'Notify me'}
          </button>
        </form>
      )}

      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        {activeCount > 0 && (
          <button onClick={clearFilters} className="bg-white border border-stone-200 text-stone-700 px-6 py-3 rounded-full font-semibold hover:border-[#1A4331] transition-all">
            Clear Filters
          </button>
        )}
        <button
          onClick={onInviteClick}
          className="bg-[#1A4331] text-white px-6 py-3 rounded-full font-semibold hover:bg-[#2A5A44] transition-all inline-flex items-center gap-2 shadow-md"
          data-testid="empty-invite-pro-btn"
        >
          <UserCirclePlus size={18} weight="duotone" /> Invite a Professional
        </button>
      </div>
    </div>
  );
};

const WellnessMarketplacePage = ({ user }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [pros, setPros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  const [filters, setFilters] = useState({
    category: searchParams.get('category') || '',
    mode: searchParams.get('mode') || '',
    city: searchParams.get('city') || '',
    country: searchParams.get('country') || '',
    q: searchParams.get('q') || '',
    verified_only: false,
  });

  const fetchPros = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const res = await axios.get(`${BACKEND_URL}/api/wellness/professionals`, { params });
      setPros(res.data);
    } catch (err) {
      setPros([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchPros(); }, [fetchPros]);

  const applyFilter = (key, value) => {
    const next = { ...filters, [key]: value };
    setFilters(next);
    const params = new URLSearchParams();
    Object.entries(next).forEach(([k, v]) => { if (v) params.set(k, v); });
    setSearchParams(params, { replace: true });
  };

  const clearFilters = () => {
    setFilters({ category: '', mode: '', city: '', country: '', q: '', verified_only: false });
    setSearchParams({}, { replace: true });
  };

  const activeCount = Object.entries(filters).filter(([, v]) => v).length;
  const currentCategory = filters.category ? getCategoryById(filters.category) : null;

  return (
    <div className="min-h-screen bg-[#FAFAF7]" data-testid="wellness-marketplace">
      <Header user={user} />

      {/* HEADER */}
      <section className="pt-10 pb-6 px-6" data-testid="marketplace-header">
        <div className="max-w-7xl mx-auto">
          <button
            onClick={() => navigate('/wellness')}
            className="text-sm text-stone-500 hover:text-[#1A4331] mb-4 inline-flex items-center gap-1"
          >
            ← Back to Wellness
          </button>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#C05C3D] mb-2">
                {currentCategory ? currentCategory.name : 'Nova Wellness'}
              </p>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#1C1917] font-heading">
                Find your <span className="italic text-[#1A4331]">wellness expert</span>
              </h1>
            </div>
            <button
              onClick={() => setShowFilters((s) => !s)}
              className="md:hidden inline-flex items-center gap-2 bg-white border border-stone-200 px-4 py-2 rounded-full font-semibold text-sm"
              data-testid="mobile-filters-btn"
            >
              <SlidersHorizontal size={16} weight="bold" />
              Filters {activeCount > 0 && <span className="bg-[#1A4331] text-white text-[10px] px-1.5 py-0.5 rounded-full">{activeCount}</span>}
            </button>
          </div>
        </div>
      </section>

      {/* SEARCH BAR */}
      <section className="px-6 pb-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center bg-white rounded-full shadow-sm border border-stone-200 p-1.5 pl-5">
            <MagnifyingGlass size={18} weight="bold" className="text-stone-400 flex-shrink-0" />
            <input
              type="text"
              value={filters.q}
              onChange={(e) => applyFilter('q', e.target.value)}
              placeholder="Search by name, type, or location..."
              className="flex-1 px-3 py-2.5 outline-none text-[14px] bg-transparent"
              data-testid="marketplace-search-input"
            />
            {filters.q && (
              <button onClick={() => applyFilter('q', '')} className="text-stone-400 hover:text-stone-700 pr-2">
                <X size={16} weight="bold" />
              </button>
            )}
          </div>
        </div>
      </section>

      <section className="px-6 pb-16" data-testid="marketplace-body">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-[240px_1fr] gap-8">
          {/* FILTERS */}
          <aside className={`${showFilters ? 'block' : 'hidden'} md:block`} data-testid="filters-panel">
            <div className="bg-white rounded-3xl p-5 border border-stone-100 space-y-6 sticky top-24">
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-bold uppercase tracking-[0.14em] text-stone-600">Filters</h3>
                {activeCount > 0 && (
                  <button onClick={clearFilters} className="text-xs text-[#C05C3D] font-semibold hover:underline" data-testid="clear-filters-btn">
                    Clear all
                  </button>
                )}
              </div>

              {/* Category */}
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-stone-500 mb-2">Category</p>
                <div className="space-y-1">
                  <button
                    onClick={() => applyFilter('category', '')}
                    className={`w-full text-left px-3 py-1.5 rounded-lg text-sm ${!filters.category ? 'bg-[#F0F7F1] text-[#1A4331] font-semibold' : 'text-stone-600 hover:bg-stone-50'}`}
                  >
                    All categories
                  </button>
                  {WELLNESS_CATEGORIES.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => applyFilter('category', c.id)}
                      className={`w-full text-left px-3 py-1.5 rounded-lg text-sm ${filters.category === c.id ? 'bg-[#F0F7F1] text-[#1A4331] font-semibold' : 'text-stone-600 hover:bg-stone-50'}`}
                      data-testid={`filter-category-${c.id}`}
                    >
                      {c.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Mode */}
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-stone-500 mb-2">Mode</p>
                <div className="flex flex-wrap gap-1.5">
                  {MODES.map((m) => (
                    <button
                      key={m.id}
                      onClick={() => applyFilter('mode', filters.mode === m.id ? '' : m.id)}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${filters.mode === m.id ? 'bg-[#1A4331] text-white border-[#1A4331]' : 'bg-white text-stone-600 border-stone-200 hover:border-[#1A4331]'}`}
                      data-testid={`filter-mode-${m.id}`}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Country */}
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-stone-500 mb-2">Country</p>
                <select
                  value={filters.country}
                  onChange={(e) => applyFilter('country', e.target.value)}
                  className="w-full bg-white border border-stone-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#1A4331]"
                  data-testid="filter-country"
                >
                  <option value="">All countries</option>
                  {COUNTRIES.map((c) => (<option key={c.code} value={c.code}>{c.name}</option>))}
                </select>
              </div>

              {/* City */}
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-stone-500 mb-2">City</p>
                <input
                  type="text"
                  value={filters.city}
                  onChange={(e) => applyFilter('city', e.target.value)}
                  placeholder="e.g., Bangalore"
                  className="w-full bg-white border border-stone-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#1A4331]"
                  data-testid="filter-city"
                />
              </div>

              {/* Verified only */}
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.verified_only}
                  onChange={(e) => applyFilter('verified_only', e.target.checked)}
                  className="w-4 h-4 accent-[#1A4331]"
                  data-testid="filter-verified"
                />
                <span className="text-sm text-stone-700">Credential Verified only</span>
              </label>
            </div>
          </aside>

          {/* RESULTS */}
          <div>
            <div className="mb-4 flex items-center justify-between text-sm text-stone-500">
              <p data-testid="results-count">
                {loading ? 'Searching…' : `${pros.length} ${pros.length === 1 ? 'expert' : 'experts'} found`}
              </p>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-5">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="bg-white rounded-3xl border border-stone-100 h-96 animate-pulse" />
                ))}
              </div>
            ) : pros.length === 0 ? (
              <NotifyMeEmpty
                activeCount={activeCount}
                filters={filters}
                clearFilters={clearFilters}
                onInviteClick={() => navigate('/wellness/join')}
              />
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-5">
                {pros.map((pro) => (
                  <ProCard
                    key={pro.professional_id}
                    pro={pro}
                    onView={(slug) => navigate(`/wellness/pro/${slug}`)}
                    onBook={(slug) => navigate(`/wellness/pro/${slug}?book=1`)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      <PremiumFooter />
    </div>
  );
};

export default WellnessMarketplacePage;
